<template>
  <div class="gs-app gs-video-editor">
    <loading :active.sync="isLoading" :is-full-page="true" :color="loaderColor" />

    <editor-top-bar
      :title="model.name || 'Edit video'"
      :is-dirty="loaded && isDirty"
      :saving="saving"
      :video-id="$route.params.id"
      @back="leave()"
      @analytics="navigate({ name: 'videoAnalytics', params: { videoId: $route.params.id } })"
      @share="setActiveTab('share')"
      @save="save()"
    />

    <div class="gs-video-editor__missing" v-if="loadFailed">
      <h2 class="gs-video-editor__missing-title">Video not found</h2>
      <p class="gs-video-editor__missing-copy">
        We could not load this video. It may have been deleted.
      </p>
      <gs-button variant="outline" @click="leave()">Back to library</gs-button>
    </div>

    <div class="gs-video-editor__body" v-else-if="loaded">
      <aside class="gs-video-editor__preview">
        <editor-preview
          :model="model"
          :mode.sync="previewMode"
          :status="status"
          :is-video-src="is_video_src"
          :encoding="isVideoEncoding"
        />
        <p class="gs-video-editor__note"><strong>{{ isModern ? 'New video settings' : 'Legacy video settings' }}</strong><br />{{ isModern ? 'VideoPlayer-style themes and animated overlays.' : 'Your original images, animations and playback settings are preserved.' }}</p>
        <p class="gs-video-editor__note">
          Changes update the preview as you type. Use Save at the top before you leave.
        </p>
      </aside>

      <section class="gs-video-editor__panel">
        <nav class="gs-video-editor__tabs" role="tablist">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            role="tab"
            :aria-selected="activeTab === tab.value ? 'true' : 'false'"
            :class="{ 'is-active': activeTab === tab.value }"
            :data-tab="tab.value"
            @click="setActiveTab(tab.value)"
          >{{ tab.label }}</button>
        </nav>

        <keep-alive>
          <component
            :is="panelComponent"
            ref="panel"
            :model="model"
            :publishes-left="publishesLeft"
            :status="status"
            @updateModel="model = $event"
            @forceRerender="forceRerender()"
            @replaced="updateVideoSource()"
            @patch-snapshot="patchSnapshot($event, model)"
            @setIsLoading="isLoading = $event"
            @on-validate="onPanelValidated($event)"
            @updatePublishLimit="updatePublishLimit($event)"
            @saved="snapshot(model)"
          />
        </keep-alive>
      </section>
    </div>

    <gs-alert-dialog
      v-model="leaveDialogOpen"
      title="Discard unsaved changes?"
      copy="Your latest edits have not been saved."
      confirm-label="Discard"
      cancel-label="Keep editing"
      variant="primary"
      @confirm="discardAndLeave()"
      @cancel="cancelLeave()"
    />
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import Loading from 'vue-loading-overlay';
import Notify from '@groovepages/gd-lib/notify';
import 'vue-loading-overlay/dist/vue-loading.css';
import themeConfig from '@/theme/themeConfig.js';
import Video from '@/models/Video';
import GetStatus from '@/models/GetStatus.js';
import dirtyState from '@/mixins/dirtyState.js';
import limitHelper from '@/mixins/limitHelper.js';
import { getMuxVideoStatus } from '@/services/muxApi';
import { isMuxPending } from '@/utils/muxSource';
import { createMuxPoller } from '@/utils/muxPoller';

import EditorTopBar from './EditorTopBar.vue';
import EditorPreview from './EditorPreview.vue';
import PlayerPanel from './player/PlayerPanel.vue';
import MarketingPanel from './MarketingPanel.vue';
import ShareEmbedPanel from './ShareEmbedPanel.vue';
import { isModernVideo, syncModernAppearance, restoreModernAppearance } from './player/newAppearance';
import { applyPlayerDefaults } from './player/playerOptions';

const axios = require('axios');

export default {
  name: 'EditorShell',
  middleware: 'auth',
  // `basic` is a bare wrapper: the layouts with a chrome header would stack a
  // second bar above this page's own sticky editor bar.
  layout: 'basic',

  components: {
    Loading,
    EditorTopBar,
    EditorPreview,
    PlayerPanel,
    MarketingPanel,
    ShareEmbedPanel,
  },

  mixins: [dirtyState, limitHelper],

  props: {
    activityCreated: {},
  },

  metaInfo() {
    return {
      title: this.model && this.model.name ? this.model.name : 'Edit video',
    };
  },

  data() {
    return {
      model: {},
      activeTab: 'player',
      previewMode: 'editor',
      loaded: false,
      loadFailed: false,
      saving: false,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
      publishesLeft: 0,
      // Ported from the wizard.
      is_video_src: false,
      status: false,
      isVideoEncoding: false,
      isUploading: true,
      state: null,
      statusTimer: null,
      muxPoller: null,
      pendingValidation: null,
    };
  },

  computed: {
    isModern() { return isModernVideo(this.model); },
    tabs() {
      return [
        { value: 'player', label: 'Player' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'share', label: 'Share & embed' },
      ];
    },

    panelComponent() {
      if (this.activeTab === 'marketing') return 'MarketingPanel';
      if (this.activeTab === 'share') return 'ShareEmbedPanel';
      return 'PlayerPanel';
    },
  },

  watch: {
    // Publishing saves itself through its own endpoint, so it must never leave
    // the editor looking as if it has unsaved changes.
    'model.is_published': function onPublishedChange() {
      this.patchSnapshot(['is_published'], this.model);
    },
  },

  beforeDestroy() {
    if (this.statusTimer) {
      clearTimeout(this.statusTimer);
      this.statusTimer = null;
    }
    if (this.muxPoller) {
      this.muxPoller.stop();
      this.muxPoller = null;
    }
  },

  beforeRouteLeave(to, from, next) {
    // Nothing is editable until the load lands, so a load failure must never
    // put the discard dialog between the user and the library.
    if (!this.loaded || !this.isDirty) return next();
    this.confirmLeave(next);
  },

  async created() {
    this.showLoader();

    // A deleted video, a video on another account (403) or any transient
    // failure must land on a readable state, not a permanent blocking spinner.
    try {
      await this.fetchActivity();

      if (this.activityCreated) {
        await this.addActivity(this.activityCreated);
      }

      // If the video is still encoding, start the poller.
      if (
        this.activityCreated == undefined &&
        [1, 2].includes(this.model.video_source_type) &&
        this.model.video_encoding &&
        this.model.video_encoding.is_complete == 0
      ) {
        await this.addActivity('encoding');
      }

      if (
        this.model.is_local === 1 &&
        this.model.video_stream_source !== null &&
        this.model.video_stream_source != ''
      ) {
        this.is_video_src = true;
      } else if (this.model.video_source == '' || this.model.video_source == null) {
        this.is_video_src = false;
      } else {
        this.is_video_src = true;
      }

      this.publishesLeft = await this.getRemainingPublishLimit();

      // Everything on screen now matches the server.
      this.snapshot(this.model);
      this.loaded = true;

      // A Mux upload/import still processing: poll (server-throttled) until it can play.
      this.startMuxPolling();
    } catch (error) {
      this.loadFailed = true;
      this.loaded = false;
    } finally {
      this.hideLoader();
    }
  },

  methods: {
    // Ported from the wizard.
    async fetchActivity() {
      this.showLoader();
      const videoId = this.$route.params.id;
      this.model = await Video.$find(videoId);

      applyPlayerDefaults(this.model, this.$set);
      restoreModernAppearance(this.model, this.$set);

      // Set default color
      this.model =
        typeof this.model.settings.color === 'undefined'
          ? {
              ...this.model,
              settings: {
                ...this.model.settings,
                color: {
                  primary: 'rgba(253, 100, 129, 1)',
                  secondary: 'rgba(255, 255, 255, 1)',
                },
              },
            }
          : this.model;

      // Set default watermark
      this.model =
        typeof this.model.settings.watermark === 'undefined'
          ? {
              ...this.model,
              settings: {
                ...this.model.settings,
                watermark: {
                  source: '',
                  position: 'top-left',
                },
              },
            }
          : this.model;

      // Everything else the Player panel binds into, filled before `created()`
      // snapshots the saved state, so a video whose payload predates one of
      // these settings does not open with unsaved changes.
      applyPlayerDefaults(this.model, this.$set);

      this.forceRerender();

      if (
        typeof this.model.is_local !== 'undefined' &&
        this.model.is_local === 1 &&
        this.model.video_stream_source == null &&
        this.model.video_stream_source === ''
      ) {
        this.model.is_local = 0;
      }

      // Check if video source is present for video player preview.
      // Wrapped in Boolean() because the original expression can land on
      // `null`/`undefined` and the preview declares this as a Boolean prop.
      this.is_video_src = Boolean(
        (this.model.video_stream_source && this.model.video_stream_source != '') ||
          (this.model.video_source && this.model.video_source != '')
      );

      this.status = !(this.model.is_local === 1 && this.model.is_available === 0);
      this.hideLoader();
    },

    // Panel validation contract: a hosted panel may expose `validate()`, which
    // runs its own rules and answers through `@on-validate` (the legacy steps
    // also return the same boolean). Tasks 6-8 keep `validate()` on their
    // panels so this gate keeps working.
    onPanelValidated(isValid) {
      if (this.pendingValidation) this.pendingValidation(isValid);
    },

    async validatePanel(component) {
      if (!component || typeof component.validate !== 'function') return true;

      let emitted = null;
      this.pendingValidation = value => {
        emitted = value;
      };
      let returned;
      try {
        returned = await component.validate();
      } finally {
        this.pendingValidation = null;
      }

      if (emitted !== null) return emitted !== false;
      return returned !== false;
    },

    async validateActivePanel() {
      const panel = this.$refs.panel;
      if (!panel) return true;

      // A panel may validate itself, or host children that do (the legacy
      // Share step validates itself).
      const targets =
        typeof panel.validate === 'function'
          ? [panel]
          : (panel.$children || []).filter(
              child => typeof child.validate === 'function'
            );

      let isValid = true;
      for (let i = 0; i < targets.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await this.validatePanel(targets[i]);
        isValid = isValid && result;
      }
      return isValid;
    },

    // Wizard parity: the step being left has to be valid before the user can
    // move on, otherwise an invalid field simply stops being validated (only
    // the active panel is checked on Save) and reaches the server.
    async setActiveTab(value) {
      if (value === this.activeTab) return;

      if (!(await this.validateActivePanel())) {
        Notify.fail('Fix the highlighted fields before switching tabs');
        return;
      }

      this.activeTab = value;
    },

    async save() {
      if (this.saving) return;

      if (!(await this.validateActivePanel())) {
        Notify.fail('Fix the highlighted fields before saving');
        return;
      }

      // The title is required no matter which tab is open: `saveBasicDetails`
      // only defaults an `undefined` name, so an empty string is stored.
      if (!this.model.name || !String(this.model.name).trim()) {
        Notify.fail('Give the video a title before saving');
        return;
      }

      this.saving = true;
      try {
        syncModernAppearance(this.model);
        await axios.post('/video_details', this.model);
        this.snapshot(this.model);
        Notify.success('Changes saved');
      } catch (error) {
        Notify.fail('We could not save your changes. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    // `beforeRouteLeave` can hold a navigation and then cancel it, which makes
    // the router's promise reject. Swallow that: it is our own guard talking.
    navigate(location) {
      const navigation = this.$router.push(location);
      if (navigation && typeof navigation.catch === 'function') {
        navigation.catch(() => {});
      }
    },

    leave() {
      this.navigate({ name: 'videos' });
    },

    discardAndLeave() {
      this.leaveDialogOpen = false;
      const next = this.pendingNext;
      this.pendingNext = null;
      if (next) next();
    },

    cancelLeave() {
      this.leaveDialogOpen = false;
      const next = this.pendingNext;
      this.pendingNext = null;
      // Release the held navigation so the router does not stay pending.
      if (next) next(false);
    },

    // Mirrors the library's method: the publish endpoint owns the count.
    updatePublishLimit(data) {
      this.publishesLeft = data.isPublished
        ? this.publishesLeft - 1
        : this.publishesLeft + 1;
    },

    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    // Ported from the wizard: encoding the video.
    addActivity(newActivity) {
      if (newActivity == 'uploading') {
        // do nothing
      } else if (newActivity == 'processing') {
        this.isVideoEncoding = true;
      } else if (newActivity == 'encoding') {
        this.isVideoEncoding = true;

        // Check status if video is getting uploaded
        this.getStatus();
      } else if (newActivity == 'uploaded') {
        this.isVideoEncoding = false;

        Swal.fire(
          this.$t('frontend_groovevideo_create_processing_complete'),
          this.$t('frontend_groovevideo_create_video_uploaded'),
          'success'
        );
      } else {
        Swal.fire(
          this.$t('frontend_groovevideo_create_unknown_backend_error'),
          this.$t('frontend_groovevideo_create_unable_upload_video'),
          'error'
        );
      }
    },

    // Ported from the wizard; the route name is now `editVideo`.
    async getStatus() {
      if (this._isDestroyed) return;
      this.state = new GetStatus({});
      this.state.vid = this.$route.params.id;

      // eslint-disable-next-line complexity
      await this.state.save().then(async response => {
        // The user may have left while the request was in flight.
        if (this._isDestroyed) return;
        if (response.status !== 'uploaded' && this.$route.name === 'editVideo') {
          if (!this.status && response.status !== 'processing') {
            /* videoplayer display */
            this.status = true;
          }
          /* fetch new video_source after upload to NFS */
          if (this.isUploading && response.status === 'encoding') {
            this.isUploading = false;
            await this.updateVideoSource();
          }
          this.repeat();
        } else if (this.$route.params.id) {
          // Video successfully uploaded
          this.isVideoEncoding = false;

          await this.updateVideoSource();
        }
      }).catch(() => {
        // One failed poll used to kill the loop silently and leave the preview
        // saying "encoding" forever. Stop cleanly and say so instead.
        if (this._isDestroyed) return;
        if (this.statusTimer) {
          clearTimeout(this.statusTimer);
          this.statusTimer = null;
        }
        this.isVideoEncoding = false;
        Notify.fail('We lost track of the encoding status. Reload to check again.');
      });
    },

    // Ported from the wizard. The player is remounted through the store key
    // rather than the old `renderComponent` flag, which tracked `is_video_src`.
    async updateVideoSource() {
      const videoId = this.$route.params.id;
      const videoDetails = await Video.$find(videoId);

      if (videoDetails.is_available != 1) {
        this.is_video_src = false;
      } else if (
        typeof videoDetails.is_local !== 'undefined' &&
        videoDetails.is_local === 1 &&
        videoDetails.video_stream_source !== null &&
        videoDetails.video_stream_source !== ''
      ) {
        this.is_video_src = true;
        this.model.video_stream_source = videoDetails.video_stream_source;
        this.model.is_local = videoDetails.is_local;
      } else if (videoDetails.video_source == '' || videoDetails.video_source == null) {
        Swal.fire(
          this.$t('frontend_groovevideo_create_unknown_backend_error'),
          this.$t('frontend_groovevideo_create_unable_upload_video'),
          'error'
        );
        this.model.video_source = videoDetails.video_source;
        this.is_video_src = false;
      } else {
        this.model.video_source = videoDetails.video_source;
        this.model.public_video_source = videoDetails.public_video_source;
        this.model.temp_video_source =
          typeof videoDetails.temp_video_source !== 'undefined'
            ? videoDetails.temp_video_source
            : null;
        this.model.video_source_type = videoDetails.video_source_type;
        this.model.public_video_source_type = videoDetails.public_video_source_type;
        this.model.is_processing = videoDetails.is_processing;
        this.is_video_src = true;
      }
      this.model.is_uploading = videoDetails.is_uploading;
      this.model.thumbnail_url = videoDetails.thumbnail_url;
      this.forceRerender();
      // The encoder, not the user, made these changes: move just these keys
      // onto the saved baseline so a poll never re-baselines unsaved edits.
      this.patchSnapshot(
        [
          'video_source',
          'public_video_source',
          'temp_video_source',
          'video_source_type',
          'public_video_source_type',
          'is_processing',
          'is_uploading',
          'thumbnail_url',
          'video_stream_source',
          'is_local',
        ],
        this.model
      );
    },

    // Ported from the wizard, with the timer held so it can be cleared.
    async repeat() {
      const self = this;
      this.statusTimer = setTimeout(async () => {
        self.statusTimer = null;
        if (self.isVideoEncoding && !self._isDestroyed) {
          await self.getStatus();
        }
      }, 1000);
    },

    startMuxPolling() {
      if (this.muxPoller || !isMuxPending(this.model)) return;
      this.muxPoller = createMuxPoller({
        videoId: this.model.id,
        fetchStatus: getMuxVideoStatus,
        onUpdate: fresh => {
          this.model.mux_status = fresh.mux_status;
          this.model.mux_error = fresh.mux_error;
          this.patchSnapshot(['mux_status', 'mux_error'], this.model);
        },
        onReady: async () => {
          this.muxPoller = null;
          this.status = true;
          await this.updateVideoSource();
        },
      });
    },

    // Remounts the real player so video.js re-reads the settings.
    forceRerender() {
      this.$store.dispatch('videoComponentKey');
    },

  },
};
</script>

<style scoped>
.gs-video-editor {
  /* `basic` layout has no scroll container of its own, so this page is the
     full-height surface and the document scrolls. */
  min-height: 100vh;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gs-video-editor__missing {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  max-width: 480px;
  margin: 96px auto;
  padding: 32px 24px;
  text-align: center;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-video-editor__missing-title {
  margin: 0;
  color: var(--gs-foreground);
  font-size: 18px;
  font-weight: 600;
}

.gs-video-editor__missing-copy {
  margin: 0 0 6px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
}

.gs-video-editor__body {
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 28px;
  align-items: start;
  max-width: 1440px;
  margin: 0 auto;
  padding: 24px 24px 56px;
}

.gs-video-editor__preview {
  position: sticky;
  top: 88px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

.gs-video-editor__note {
  margin: 0;
  color: var(--gs-muted-foreground);
  font-size: 13px;
}

.gs-video-editor__panel {
  min-width: 0;
  overflow: hidden;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

/* Theme, autoplay and play button mark the player panel `.is-picker-section`
   while their popup is open. The panel has to let that popup out; the other
   tabs keep the clip so long unbroken text stays inside the column. */
.gs-video-editor__panel:has(.is-picker-section) {
  overflow: visible;
}

.gs-video-editor__tabs {
  display: flex;
  gap: 4px;
  padding: 8px 12px 0;
  border-bottom: 1px solid var(--gs-border);
}

.gs-video-editor__tabs button {
  padding: 10px 14px;
  color: var(--gs-muted-foreground);
  font-size: 14px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  cursor: pointer;
}

.gs-video-editor__tabs button:hover {
  color: var(--gs-foreground);
}

.gs-video-editor__tabs button:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px var(--gs-ring);
}

.gs-video-editor__tabs button.is-active {
  color: var(--gs-primary);
  border-bottom-color: var(--gs-primary);
}

/* The legacy Share step still carries its own card chrome. */
.gs-video-editor__panel ::v-deep .gv-editor-step,
.gs-video-editor__panel ::v-deep .gv-editor-display {
  box-shadow: none;
}

@media (max-width: 1023px) {
  .gs-video-editor__body {
    grid-template-columns: minmax(0, 1fr);
    padding: 16px 16px 40px;
  }

  .gs-video-editor__preview {
    position: static;
  }
}
</style>

<!--
  Unscoped, and deliberately verbatim from the wizard this page replaces:
  SweetAlert mounts on `document.body`, outside `.gs-app`, so design tokens do
  not resolve there. These literal colours are relocated legacy styling, not
  new design.
-->
<style lang="scss">
.swal2-modal {
  h2 {
    font-size: 24px !important;
    line-height: 26px !important;
  }
  .swal2-content {
    font-size: 20px !important;
    line-height: 24px !important;
  }
  .swal2-icon {
    margin: 10px auto !important;
  }
  .swal2-actions {
    .swal2-confirm {
      background: #fd6481 !important;
      margin: 0;
      padding: 10px 0;
      font-size: 14px;
      width: 115px;
      &:active,
      &:focus {
        outline: none;
      }
    }
    .swal2-cancel {
      background: #000000 !important;
      padding: 10px 0;
      font-size: 14px;
      margin-left: 20px;
      width: 115px;
      &:active,
      &:focus {
        outline: none;
      }
    }
  }
}
</style>
